import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DropdownInputComponent } from '../../../../shared/dropdown-input/dropdown-input.component';
import { TrialAutocompleteComponent } from '../../../../shared/trial-autocomplete/trial-autocomplete.component';
import { SelectOption } from '../../../../core/models/select-option.model';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ObjectHelper } from '../../../../core/helpers/object.helper';
import { BehaviorSubject } from 'rxjs';
import { ModalComponent } from '../../../../shared/modal/modal.component';
import { SiteContactSiteViewModel } from '../SiteContactSite.view.model';
import { AlertService } from '../../../../shared/alert/alert.service';
import { TrialService } from 'app/core/services/trial.service';
import { SiteDropdownOption } from "../../../../shared/site-dropdown/site-dropdown-option.model";

@Component({
  selector: 'app-manage-user-sites',
  templateUrl: './manage-user-sites.component.html',
  styleUrls: ['./manage-user-sites.component.scss']
})
export class ManageUserSitesComponent implements OnInit {
  @ViewChild('unassignModel') public unassignModel: ModalComponent;
  @ViewChild('roleDropdown') roleDropdown: DropdownInputComponent;
  @ViewChild('trialAutocomplete') trialAutocomplete: TrialAutocompleteComponent;

  @Output('assignedSitesChanged') assignedSitesChanged = new BehaviorSubject<SiteContactSiteViewModel[]>(null);
  @Output('hideFormButtons') hideFormButtons = new EventEmitter<boolean>();

  @Input('contactName') contactName: string = null;

  showAssignForm = true;
  roleOptions: SelectOption[] = [
    { value: '0', text: 'Principle Investigator' },
    { value: '1', text: 'Sub Investigator' },
    { value: '2', text: 'Site Coordinator' },
    { value: '3', text: 'External Clinician' }
  ];
  assignedSites: SiteContactSiteViewModel[] = [];

  assignSiteForm: UntypedFormGroup;
  objectHelper = ObjectHelper;

  modifyAtIndex: number = -1;
  modifySiteIrgSiteId: string = '';
  modifySiteName: string = '';
  modifyTrialCode: string = '';
  selectedSite: SiteDropdownOption = null;

  constructor(private alertService: AlertService, private trialService: TrialService) { }

  ngOnInit(): void {
    this.assignSiteForm = new UntypedFormGroup({
      trialId: new UntypedFormControl('', Validators.required),
      siteId: new UntypedFormControl('', Validators.required),
      role: new UntypedFormControl('', Validators.required),
      blinded: new UntypedFormControl(false)
    });
  }

  onHandleEditAtIndex(index: number) {
    let site = this.assignedSites[index];

    this.modifyAtIndex = index;
    this.modifyTrialCode = site.trialNickname?.length ?
      `${site.trialCode} ${site.trialNickname} - ${site.trialOpportunityNumber}` :
      `${site.trialCode} ${site.trialOpportunityNumber}`;
    this.modifySiteName = site.siteName;
    this.modifySiteIrgSiteId = site.irgSiteId;
    this.assignSiteForm.patchValue({ role: site.role.toString() });
    this.roleDropdown.setValue(site.role.toString());

    this.showAssignForm = false;
    this.hideFormButtons.emit(true);
    this.assignSiteForm.patchValue({
      trialId: this.assignedSites[index].trialId,
      siteId: this.assignedSites[index].siteId,
      role: this.assignedSites[index].role,
      blinded: this.assignedSites[index].blinded
    });
  }

  setAssignedSites(sites: SiteContactSiteViewModel[]) {
    this.assignedSites = sites;

    // Raise event to let subscribers know that the assigned sites have changed
    this.assignedSitesChanged.next(this.assignedSites);
  }

  onHandleShowAssignTrial() {
    this.showAssignForm = false;
    this.hideFormButtons.emit(true);
  }

  onHandleReturnToForm() {
    this.showAssignForm = true;
    this.hideFormButtons.emit(false);

    this.assignSiteForm.reset({ blinded: false });
    this.trialAutocomplete.reset();
    this.roleDropdown.reset();
    this.modifyAtIndex = -1;
    this.hideFormButtons.emit(false);
  }

  onHandleDeleteConfirmed(index: number) {
    this.assignedSites[index].deleted = true;

    // Raise event to let subscribers know that the assigned sites have changed
    this.assignedSitesChanged.next(this.assignedSites);

    // Reset the unassigning Site flag
    this.unassignModel.deleteIndex = -1;

    this.hideFormButtons.emit(false);

    // Hide the modal window
    this.unassignModel.hide();
  }

  onHandleRemoveAtIndex(index: number) {
    this.unassignModel.resetDeleteIndexAndId();
    this.unassignModel.show();
  }

  onHandleUpdateSite(index: number) {
    let role = this.roleOptions.find(x => String(x.value) === String(this.assignSiteForm.get('role').value));

    this.assignedSites[index].role = +role.value;
    this.assignedSites[index].roleName = role.text;
    this.assignedSites[index].blinded = this.assignSiteForm.get('blinded').value;

    this.showAssignForm = true;
    this.modifyAtIndex = -1;
    this.modifySiteName = '';
    this.modifyTrialCode = '';

    // Raise event to let subscribers know that the assigned sites have changed
    this.assignedSitesChanged.next(this.assignedSites);
    this.hideFormButtons.emit(false);
  }

  onHandleSiteSelected(dropdownValue: SiteDropdownOption) {
    this.selectedSite = dropdownValue;
  }

  onHandleAssignSite() {
    if (!this.selectedSite)
      return;

    // First let's make sure this site isn't already assigned
    let existingSite = this.assignedSites.find(x => x.siteId === this.selectedSite.id && x.trialId === this.assignSiteForm.get('trialId').value);

    this.trialService.retrieveTrial(this.assignSiteForm.get('trialId').value).subscribe({
      next: response => {
        if (ObjectHelper.isUndefinedOrNull(existingSite)) {
          this.assignedSites.push(new SiteContactSiteViewModel({
            id: '',
            irgSiteId: this.selectedSite.externalId,
            siteId: this.selectedSite.id,
            trialId: this.assignSiteForm.get('trialId').value,
            trialCode: response.code,
            trialNickname: response.nickname,
            trialOpportunityNumber: response.opportunityNumber,
            role: this.assignSiteForm.get('role').value,
            siteName: this.selectedSite.name,
            roleName: this.roleOptions.find(x => x.value === this.assignSiteForm.get('role').value).text,
            deleted: false,
            blinded: this.assignSiteForm.get('blinded').value
          }));

          // Raise event to let subscribers know that the assigned sites have changed
          this.assignedSitesChanged.next(this.assignedSites);
        } else {
          // Undelete the existing site
          if (existingSite.deleted) {
            existingSite.deleted = false;
          } else {
            this.alertService.showWarningAlert('This trial/site combination is already assigned to this contact.');
          }
        }

        this.assignSiteForm.reset({ blinded: false });
        this.trialAutocomplete.reset();
        this.roleDropdown.reset();
        this.showAssignForm = true;
        this.hideFormButtons.emit(false);
      }
    });
  }
}
